<template>
  <div
    class="transition-all duration-200"
    :class="[
      variant === 'primary'
        ? 'rounded-base border border-light-200 bg-white p-1.5 dark:border-grey-dark dark:bg-dark'
        : '-mx-1 overflow-x-auto px-1',
      variant === 'primary' && compact ? 'shadow-lg' : 'shadow-sm',
    ]"
    :data-compact="variant === 'primary' && compact ? 'true' : 'false'"
  >
    <div
      :id="`${baseId}-tablist`"
      role="tablist"
      :aria-label="ariaLabel"
      :aria-orientation="isHorizontal ? 'horizontal' : 'vertical'"
      class="flex"
      :class="
        variant === 'primary' && !compact
          ? 'flex-col gap-1'
          : variant === 'primary'
            ? 'flex-row justify-around gap-1'
            : 'min-w-max flex-row gap-2'
      "
    >
      <button
        v-for="(item, index) in items"
        :id="`${baseId}-${item.id}-tab`"
        :key="item.id"
        type="button"
        role="tab"
        :tabindex="activeId === item.id ? 0 : -1"
        :aria-selected="activeId === item.id"
        :aria-controls="`${resolvedPanelBaseId}-${item.id}-panel`"
        :title="variant === 'primary' && compact ? item.label : undefined"
        class="flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-md text-sm font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:focus-visible:outline-gold"
        :class="[
          variant === 'primary'
            ? compact
              ? 'min-w-11 flex-1 justify-center p-2.5'
              : 'w-full justify-start px-4 py-3 sm:px-5'
            : 'justify-center px-3 py-2.5',
          activeId === item.id
            ? variant === 'primary'
              ? 'bg-black text-white dark:bg-white dark:text-black'
              : 'bg-gold text-black'
            : 'text-black hover:bg-light-200 dark:text-white dark:hover:bg-grey-dark',
        ]"
        @click="selectTab(item.id)"
        @keydown="handleKeydown($event, index)"
      >
        <Icon
          v-if="item.icon"
          :icon="item.icon"
          class="leading-none"
          :class="variant === 'primary' && compact ? 'text-xl' : 'text-base'"
          aria-hidden="true"
        />
        <span :class="variant === 'primary' && compact ? 'sr-only' : undefined">
          {{ item.label }}
        </span>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GuideNavigationItem } from "@/types";

const props = withDefaults(
  defineProps<{
    items: GuideNavigationItem[];
    activeId: string;
    ariaLabel: string;
    baseId: string;
    panelBaseId?: string;
    variant?: "primary" | "secondary";
    compact?: boolean;
  }>(),
  {
    variant: "primary",
    compact: false,
    panelBaseId: undefined,
  },
);

const emit = defineEmits<{
  select: [id: string];
}>();

const isHorizontal = computed(
  () => props.variant === "secondary" || props.compact,
);
const resolvedPanelBaseId = computed(() => props.panelBaseId ?? props.baseId);

const selectTab = (id: string) => {
  emit("select", id);
};

const focusTab = async (index: number) => {
  const item = props.items[index];
  if (!item) return;

  selectTab(item.id);
  await nextTick();
  document.getElementById(`${props.baseId}-${item.id}-tab`)?.focus();
};

const handleKeydown = (event: KeyboardEvent, currentIndex: number) => {
  if (!props.items.length) return;

  let nextIndex: number | undefined;
  const nextKey = isHorizontal.value ? "ArrowRight" : "ArrowDown";
  const previousKey = isHorizontal.value ? "ArrowLeft" : "ArrowUp";

  if (event.key === nextKey) {
    nextIndex = (currentIndex + 1) % props.items.length;
  } else if (event.key === previousKey) {
    nextIndex = (currentIndex - 1 + props.items.length) % props.items.length;
  } else if (event.key === "Home") {
    nextIndex = 0;
  } else if (event.key === "End") {
    nextIndex = props.items.length - 1;
  }

  if (nextIndex === undefined) return;

  event.preventDefault();
  void focusTab(nextIndex);
};
</script>
